// Users per organisation. Outlet: the demo user (admin) + the 12 story staff.
// Recycler: the demo user + 10 drivers + 2 operations managers.
// Each user carries flat `data.permissions` expanded from the Zero role templates (so the
// role column infers correctly) and `data.permissions_legacy` role dicts.

import {
  DEMO_DRIVERS,
  DEMO_OUTLET_ORG_ID,
  DEMO_OUTLET_STAFF,
  DEMO_RECYCLER_MANAGERS,
  DEMO_RECYCLER_ORG_ID,
  DEMO_TRUCKS,
  DEMO_USER,
  buildFakeJwtPayload,
  daysAgo,
  iso,
  ids,
} from '../core';
import type { RegisterRequest, UpdateUserRequest, UserData, UserSchema } from '../../types/auth.type';
import {
  expandRoleForSites,
  legacyRoleKey,
  legacyRoleToCanonical,
  type CanonicalRole,
  type RoleOrgType,
} from '../../auth/role-templates';
import { orgTypeFor, splitName } from './helpers';

const SOUTH_ASIAN: Record<string, string> = {
  'Imran Siddiqui': 'Pakistan',
  'Rahul Menon': 'India',
  'Muhammad Asif': 'Pakistan',
  'Rashid Khan': 'Pakistan',
  'Sajid Hussain': 'Pakistan',
  'Anil Kumar': 'India',
  'Shafiq Rahman': 'Bangladesh',
};
const WOMEN = new Set(['Noura Al-Harbi', 'Sara Al-Dosari', 'Hind Al-Ghamdi', 'Reem Al-Subaie', 'Mona Al-Fahad']);

/** Demo user: wildcard on both orgs, all sites. */
function demoUserSchema(): UserSchema {
  const payload = buildFakeJwtPayload();
  return {
    id: DEMO_USER.id,
    sub: DEMO_USER.id,
    username: DEMO_USER.username,
    email: DEMO_USER.email,
    phone: DEMO_USER.phone,
    status: 'active',
    data: {
      first_name: 'Demo',
      last_name: 'User',
      gender: 'Male',
      nationality: 'Saudi Arabia',
      employee_level: 'Senior',
      permissions: [...payload.permissions],
      sites: { ...payload.sites },
      permissions_legacy: {
        [DEMO_OUTLET_ORG_ID]: ['waste_outlet_admin'],
        [DEMO_RECYCLER_ORG_ID]: ['recycler_admin'],
      },
    },
    created_at: iso(daysAgo(430)),
    updated_at: iso(daysAgo(3)),
  };
}

export function currentDemoUser(): UserSchema {
  return demoUserSchema();
}

function roleData(orgType: RoleOrgType, orgId: string, role: CanonicalRole, siteIds: string[] | '*'): Pick<UserData, 'permissions' | 'sites' | 'permissions_legacy'> {
  const sites = siteIds === '*' ? ['*'] : siteIds;
  return {
    permissions: expandRoleForSites(orgType, role, orgId, siteIds),
    sites: { [orgId]: sites },
    permissions_legacy: { [orgId]: [legacyRoleKey(orgType, role)] },
  };
}

function birthDate(seed: number): string {
  return `${1978 + (seed * 7) % 22}-${String(1 + (seed * 5) % 12).padStart(2, '0')}-${String(1 + (seed * 11) % 27).padStart(2, '0')}`;
}

function outletUsers(): UserSchema[] {
  const orgType = 'waste_outlet' as RoleOrgType;
  const staff = DEMO_OUTLET_STAFF.map((s, i): UserSchema => {
    const isAllSites = s.site_ids.length >= 6;
    // Head of Operations administers the org; one coordinator has a custom grant; one associate left.
    const role: CanonicalRole = i === 0 ? 'admin' : s.role;
    const sites: string[] | '*' = role === 'admin' || isAllSites ? '*' : s.site_ids;
    const base = roleData(orgType, DEMO_OUTLET_ORG_ID, role, sites);
    const permissions =
      s.full_name === 'Reem Al-Subaie'
        ? [...(base.permissions as string[]), `recyclee.${DEMO_OUTLET_ORG_ID}.*.esg_report.read`, `recyclee.${DEMO_OUTLET_ORG_ID}.*.dashboard.read`, `recyclee.${DEMO_OUTLET_ORG_ID}.*.report.list`, `recyclee.${DEMO_OUTLET_ORG_ID}.*.report.read`]
        : base.permissions;
    const { first_name, last_name } = splitName(s.full_name);
    return {
      id: s.id,
      sub: s.id,
      username: s.email,
      email: s.email,
      phone: s.phone,
      status: s.full_name === 'Imran Siddiqui' ? 'inactive' : 'active',
      data: {
        first_name,
        last_name,
        name_ar: s.full_name_ar,
        job_title: s.job_title,
        job_title_ar: s.job_title_ar,
        gender: WOMEN.has(s.full_name) ? 'Female' : 'Male',
        nationality: SOUTH_ASIAN[s.full_name] ?? 'Saudi Arabia',
        date_of_birth: birthDate(i + 3),
        employee_level: role === 'admin' || role === 'ops_manager' ? 'Senior' : s.job_title.includes('Manager') || s.job_title.includes('Supervisor') ? 'Mid' : 'Line',
        iban: `SA03 8000 0000 6080 1016 ${String(7500 + i * 13).padStart(4, '0')}`,
        ...base,
        permissions,
      },
      created_at: iso(daysAgo(400 - i * 23)),
      updated_at: iso(daysAgo(30 - i * 2)),
    };
  });
  return [demoUserSchema(), ...staff];
}

function recyclerUsers(): UserSchema[] {
  const orgType = 'recycler' as RoleOrgType;
  const managers = DEMO_RECYCLER_MANAGERS.map((m, i): UserSchema => {
    const { first_name, last_name } = splitName(m.full_name);
    return {
      id: m.id,
      sub: m.id,
      username: m.email,
      email: m.email,
      phone: m.phone,
      status: 'active',
      data: {
        first_name,
        last_name,
        name_ar: m.full_name_ar,
        job_title: m.job_title,
        gender: WOMEN.has(m.full_name) ? 'Female' : 'Male',
        nationality: 'Saudi Arabia',
        date_of_birth: birthDate(40 + i),
        employee_level: 'Senior',
        ...roleData(orgType, DEMO_RECYCLER_ORG_ID, 'ops_manager', '*'),
      },
      created_at: iso(daysAgo(560 - i * 90)),
      updated_at: iso(daysAgo(14 + i)),
    };
  });
  const drivers = DEMO_DRIVERS.map((d, i): UserSchema => {
    const { first_name, last_name } = splitName(d.full_name);
    const truck = DEMO_TRUCKS.find((t) => t.driver_id === d.id);
    return {
      id: d.id,
      sub: d.id,
      username: d.email,
      email: d.email,
      phone: d.phone,
      status: d.status === 'off_duty' ? 'inactive' : 'active',
      data: {
        first_name,
        last_name,
        name_ar: d.full_name_ar,
        job_title: 'Truck Driver',
        gender: 'Male',
        nationality: SOUTH_ASIAN[d.full_name] ?? 'Saudi Arabia',
        date_of_birth: birthDate(60 + i),
        employee_level: 'Line',
        driver_license_number: d.licence_no,
        driver_license_expiry: d.licence_expiry,
        vehicle_registration_number: truck?.plate_number ?? null,
        city: d.city,
        ...roleData(orgType, DEMO_RECYCLER_ORG_ID, 'staff', '*'),
      },
      created_at: iso(daysAgo(500 - i * 31)),
      updated_at: iso(daysAgo(9 + i)),
    };
  });
  return [demoUserSchema(), ...managers, ...drivers];
}

let usersByOrg: Record<string, UserSchema[]> = {
  [DEMO_OUTLET_ORG_ID]: outletUsers(),
  [DEMO_RECYCLER_ORG_ID]: recyclerUsers(),
};
let createdCount = 0;

export function resetUsers(): void {
  usersByOrg = { [DEMO_OUTLET_ORG_ID]: outletUsers(), [DEMO_RECYCLER_ORG_ID]: recyclerUsers() };
  createdCount = 0;
}

export function usersFor(orgId: string): UserSchema[] {
  return usersByOrg[orgId] ?? [];
}

export function findUser(orgId: string, userId: string): UserSchema | undefined {
  return usersFor(orgId).find((u) => (u.id ?? u.sub) === userId);
}

function isUsernameTaken(username: string): boolean {
  const name = username.trim().toLowerCase();
  return Object.values(usersByOrg).some((list) => list.some((u) => u.username.toLowerCase() === name));
}

/** Org a register payload targets: its permission keys / flat strings, else the fallback. */
function targetOrg(data: UserData | undefined, fallback: string | null): string | null {
  const perms = data?.permissions;
  if (perms && !Array.isArray(perms)) {
    const key = Object.keys(perms).find((k) => k !== '*');
    if (key) return key;
  }
  if (Array.isArray(perms)) {
    const hit = perms.find((p) => p.startsWith('recyclee.') && !p.startsWith('recyclee.admin.'));
    if (hit) return hit.split('.')[1] ?? fallback;
  }
  return fallback;
}

/**
 * Turn the form's permissions payload into what the API stores: a legacy role
 * dict becomes flat template permissions (+ `permissions_legacy`); a flat list
 * (advanced editor) is kept as is.
 */
function normalisePermissions(
  orgId: string,
  incoming: UserData['permissions'] | undefined,
  sites: string[] | undefined,
  previous?: UserData,
): Pick<UserData, 'permissions' | 'permissions_legacy'> {
  const orgType = orgTypeFor(orgId) as RoleOrgType | null;
  if (incoming && !Array.isArray(incoming) && orgType) {
    const legacy = incoming[orgId] ?? [];
    const canonical = legacy.map(legacyRoleToCanonical).find((r): r is CanonicalRole => r !== null);
    if (canonical) {
      const siteScope: string[] | '*' = !sites || sites.includes('*') ? '*' : sites;
      return {
        permissions: expandRoleForSites(orgType, canonical, orgId, siteScope),
        permissions_legacy: { [orgId]: legacy },
      };
    }
    return { permissions: previous?.permissions ?? [], permissions_legacy: { [orgId]: legacy } };
  }
  if (Array.isArray(incoming)) {
    return { permissions: [...incoming], permissions_legacy: previous?.permissions_legacy };
  }
  return { permissions: previous?.permissions ?? [], permissions_legacy: previous?.permissions_legacy };
}

export class DemoUserError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export function registerUser(body: RegisterRequest, fallbackOrgId: string | null): UserSchema {
  const orgId = targetOrg(body.data, fallbackOrgId);
  if (!orgId || !usersByOrg[orgId]) throw new DemoUserError(400, 'Organization not found');
  if (!body.username?.trim()) throw new DemoUserError(400, 'Username is required');
  if (isUsernameTaken(body.username)) throw new DemoUserError(400, 'A user with this username already exists');
  createdCount += 1;
  const id = ids.user(createdCount);
  const now = iso(new Date());
  const rest: UserData = { ...(body.data ?? {}) };
  delete rest.permissions;
  const sites = ['*'];
  const user: UserSchema = {
    id,
    sub: id,
    username: body.username.trim(),
    email: body.username.trim(),
    phone: body.phone ?? null,
    status: 'active',
    data: {
      ...rest,
      ...normalisePermissions(orgId, body.data?.permissions, sites),
      sites: { [orgId]: sites },
    },
    created_at: now,
    updated_at: now,
  };
  usersByOrg = { ...usersByOrg, [orgId]: [...usersByOrg[orgId], user] };
  return user;
}

export function updateUser(orgId: string, userId: string, body: UpdateUserRequest): UserSchema | null {
  const current = findUser(orgId, userId);
  if (!current) return null;
  const incoming = body.data ?? {};
  const { permissions: incomingPerms, ...rest } = incoming;
  const prevData = current.data ?? {};
  const sites = prevData.sites?.[orgId];
  const next: UserSchema = {
    ...current,
    ...(body.username ? { username: body.username, email: body.username } : {}),
    ...(body.phone !== undefined ? { phone: body.phone } : {}),
    ...(body.status ? { status: body.status } : {}),
    data: {
      ...prevData,
      ...rest,
      ...(incomingPerms !== undefined ? normalisePermissions(orgId, incomingPerms, sites, prevData) : {}),
    },
    updated_at: iso(new Date()),
  };
  usersByOrg = { ...usersByOrg, [orgId]: usersFor(orgId).map((u) => ((u.id ?? u.sub) === userId ? next : u)) };
  return next;
}

/** PUT /user/{org}/{user}/sites — replaces the branch list and re-scopes template roles. */
export function replaceUserSites(orgId: string, userId: string, siteIds: string[]): UserSchema | null {
  const current = findUser(orgId, userId);
  if (!current) return null;
  const sites = siteIds.length ? [...siteIds] : [];
  const prevData = current.data ?? {};
  const legacy = prevData.permissions_legacy?.[orgId];
  const rescoped = legacy ? normalisePermissions(orgId, { [orgId]: legacy }, sites, prevData) : {};
  const next: UserSchema = {
    ...current,
    data: { ...prevData, ...rescoped, sites: { ...(prevData.sites ?? {}), [orgId]: sites } },
    updated_at: iso(new Date()),
  };
  usersByOrg = { ...usersByOrg, [orgId]: usersFor(orgId).map((u) => ((u.id ?? u.sub) === userId ? next : u)) };
  return next;
}

/** Executes an approved `user.create` request: the requested person joins the org. */
export function addApprovedUser(orgId: string, payload: Record<string, unknown>): UserSchema | null {
  const username = String(payload.username ?? '').trim();
  if (!username || !usersByOrg[orgId] || isUsernameTaken(username)) return null;
  const orgType = orgTypeFor(orgId) as RoleOrgType | null;
  const role = (payload.role as CanonicalRole | undefined) ?? 'staff';
  const siteIds = Array.isArray(payload.collection_site_ids) ? (payload.collection_site_ids as string[]) : ['*'];
  createdCount += 1;
  const id = ids.user(createdCount);
  const now = iso(new Date());
  const user: UserSchema = {
    id,
    sub: id,
    username,
    email: username,
    phone: typeof payload.phone === 'string' ? payload.phone : null,
    status: 'active',
    data: {
      first_name: typeof payload.first_name === 'string' ? payload.first_name : null,
      last_name: typeof payload.last_name === 'string' ? payload.last_name : null,
      gender: typeof payload.gender === 'string' ? payload.gender : null,
      nationality: typeof payload.nationality === 'string' ? payload.nationality : null,
      employee_level: 'Line',
      ...(orgType ? roleData(orgType, orgId, role, siteIds.includes('*') ? '*' : siteIds) : {}),
    },
    created_at: now,
    updated_at: now,
  };
  usersByOrg = { ...usersByOrg, [orgId]: [...usersByOrg[orgId], user] };
  return user;
}

/** Username of any known person (approvals show who acted). */
export function usernameOf(userId: string | null | undefined): string | null {
  if (!userId) return null;
  for (const list of Object.values(usersByOrg)) {
    const hit = list.find((u) => (u.id ?? u.sub) === userId);
    if (hit) return hit.username;
  }
  return null;
}
